import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography
} from '@mui/material'
import PropTypes from 'prop-types'

const AutomationDialog = ({
  open,
  onClose,
  automation,
  templates,
  onSave,
  isEditing = false
}) => {
  const [localAutomation, setLocalAutomation] = React.useState({
    name: '',
    type: 'plan_usage',
    enabled: true,
    template_id: null,
    config: {
      trigger: { threshold: 80, metric: 'plan_usage' }
    }
  })

  React.useEffect(() => {
    if (automation) {
      setLocalAutomation(automation)
    } else {
      setLocalAutomation({
        name: '',
        type: 'plan_usage',
        enabled: true,
        template_id: null,
        config: {
          trigger: { threshold: 80, metric: 'plan_usage' }
        }
      })
    }
  }, [automation, open])

  const handleSave = () => {
    const automationToSave = { ...localAutomation }
    let threshold = automationToSave.config?.trigger?.threshold
    
    if (threshold === '' || threshold === null || threshold === undefined) {
      threshold = 80
    } else if (typeof threshold === 'string') {
      threshold = parseInt(threshold)
      if (isNaN(threshold) || threshold < 1 || threshold > 100) {
        threshold = 80
      }
    }
    
    automationToSave.config.trigger.threshold = threshold
    onSave(automationToSave)
  }

  const updateAutomation = (updates) => {
    setLocalAutomation(prev => ({ ...prev, ...updates }))
  }

  const updateTrigger = (triggerUpdates) => {
    setLocalAutomation(prev => ({
      ...prev,
      config: {
        ...prev.config,
        trigger: { ...prev.config.trigger, ...triggerUpdates }
      }
    }))
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { backgroundColor: 'var(--color-card-bg)', color: 'var(--color-text-primary)' } }}
    >
      <DialogTitle style={{ color: 'var(--color-text-primary)' }}>
        {isEditing ? 'Edit Automation' : 'Create New Automation'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Automation Name"
          fullWidth
          value={localAutomation.name}
          onChange={(e) => updateAutomation({ name: e.target.value })}
          required
        />

        <FormControl fullWidth margin="dense">
          <InputLabel>Automation Type</InputLabel>
          <Select
            value={localAutomation.type}
            onChange={(e) => updateAutomation({ type: e.target.value })}
          >
            <MenuItem value="plan_usage">Plan Usage Alert</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>Email Template</InputLabel>
          <Select
            value={localAutomation.template_id || ''}
            onChange={(e) => updateAutomation({ template_id: e.target.value || null })}
          >
            <MenuItem value="">
              <em>Default Template</em>
            </MenuItem>
            {templates.map(template => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {localAutomation.type === 'plan_usage' && (
          <>
            <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
              Trigger Configuration
            </Typography>
            <TextField
              margin="dense"
              label="Usage Threshold (%)"
              type="text"
              fullWidth
              value={localAutomation.config?.trigger?.threshold ?? 80}
              onChange={(e) => {
                const value = e.target.value
                if (value === '') {
                  updateTrigger({ threshold: '' })
                } else if (/^\d*$/.test(value)) {
                  updateTrigger({ threshold: value })
                }
              }}
              helperText="Enter a number between 1 and 100"
            />
          </>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={localAutomation.enabled}
              onChange={(e) => updateAutomation({ enabled: e.target.checked })}
              color="primary"
            />
          }
          label="Enabled"
          style={{ marginTop: 16 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={!localAutomation.name.trim()}
        >
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

AutomationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automation: PropTypes.object,
  templates: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  isEditing: PropTypes.bool
}

export default AutomationDialog